"use client";

import dynamic from "next/dynamic";
import { useState, type ComponentType, type FocusEvent, type PointerEvent } from "react";

import BirthToolFormShell from "@/components/tool-shell/BirthToolFormShell";
import { useDeferredToolActivation } from "@/components/tool-shell/useDeferredToolActivation";

import { FINDER_FORM, FINDER_FRAME } from "./[slug]/_finder-form";
import type { AfterVerdictProps } from "./[slug]/NakshatraFinder";

// The /nakshatra/ hub finder boundary (the #2460 pattern, a copy of the
// /nakshatra/<slug>/ NakshatraFinderLazy): the hub page imports only this
// module, and it owns the one dynamic() to the SAME island the 27 slug pages
// load (./[slug]/NakshatraFinder), in its no-page mode: no page nakshatra, so
// every verdict names the visitor's nakshatra and links its profile. Both
// routes share one async chunk. The form, place search, kundali client,
// domain data and verdict stay out of the hub's first load.
//
// Activation is element-scoped: a reader scrolling the hub never downloads
// the island; a tap, focus or submit on the shell does.
//
// The slot keeps the slug finder's floors, 600 px on phones and 500 px from
// the sm breakpoint. Every hub state is one the slug finder already has or a
// shorter one: the shell, the form and the form with an API error are
// identical (same FINDER_FORM, same island), and each hub verdict is the slug
// page's "not this page" verdict minus its " - not <page>" suffix (exact,
// whole-day) or with a one-line summary in place of the page summary
// (windows). Re-measured on the hub 2026-10-20 (next dev + Playwright,
// POST /kundali mocked with a 1.5 s delay, longest names, saved place, service
// workers blocked) at 320 / 360 / 480 / 640 / 1280 wide: the shell 436 / 436 /
// 420 / 420 / 420; the form with an API error, the tallest state, 522 / 522 /
// 488 / 448 / 448; the longest verdict (three day windows) 509 / 427 / 388 /
// 404 / 404; the exact verdict with a boundary note 445 / 424 / 339 / 376 /
// 376. The 2026-10-06 slug measurement found the error form up to 586 px at
// 320, so the slug floors stay. Content below the slot moved 0 px and no
// layout shift without recent input was recorded in any state at any width.
// The open "Don't know your birth time?" explainer (570 px at 320, 528 at 640)
// is folded on submit (collapseTimeUnknownOnSubmit), as on the slug pages.
// translate="no" covers the shell, form and verdict
// (tests/translate-safe-islands.test.ts).
//
// The report offer (NakshatraHubFinderOffer, the /nakshatra-calculator's
// Brihat Kundali bridge) is the hub's own async chunk, loaded by a plain
// import() so the slug pages never load it and the hub's first load never
// carries it. Its chunk starts loading on the form's submit, and the island
// awaits it (afterVerdictReady) alongside the engine call, so the card paints
// in the same commit as the answer, never a beat after it. The island renders
// it after the answer's own reserved frame (SLOT_FLOORS, passed as
// frameClassName), not inside it, and scrolls the answer to the top of the
// viewport before that commit paints, as the calculator does. The submit
// itself reserves the answer plus the offer (SUBMIT_RESERVE), so the reply
// fills space already there: content below the slot is on screen at submit
// time on a phone, and no scroll can keep a late growth from moving it. Until
// 2026-10-20 the card came from a second next/dynamic() and mounted ~300 ms
// after the answer inside the answer's flex column: the answer box shrank and
// the column grew, a 0.17-0.30 CLS 2 s after the submit (Pixel 7, 360 / 640).
// The slot floors reserve the form and the answer, not the offer, so a
// visitor who never gets an answer never sees blank space.
const SHELL_PROPS = { ...FINDER_FORM, className: FINDER_FRAME } as const;

/** The slug finder's floors: 600 px on phones, 500 px from the sm breakpoint. */
const SLOT_FLOORS = "flex min-h-[600px] flex-col sm:min-h-[500px]";

/**
 * The answer frame plus the report offer, reserved on the first submit (see
 * the island's submitReserveClassName). Measured 2026-10-20 (next dev +
 * Playwright, Pixel 7, real engine, 800 px tall): the answer frame (its
 * floor; every verdict is shorter) plus the offer is 1673 px at 320 wide,
 * 1519 at 360, 1438 at 480 and 1352 at 640 / 1280, so the reserve is 1720 /
 * 1560 from 360 / 1400 from sm. With it the only post-submit shift is the
 * growth inside the submit's input window (hadRecentInput): post-submit CLS
 * 0 at 320 / 360 / 640 / 1280, with and without a birth time.
 */
const SUBMIT_RESERVE = "min-h-[1720px] min-[360px]:min-h-[1560px] sm:min-h-[1400px]";

const NakshatraFinder = dynamic(() => import("./[slug]/NakshatraFinder"), {
  ssr: false,
  loading: () => <BirthToolFormShell {...SHELL_PROPS} />,
});

type HubOfferComponent = ComponentType<AfterVerdictProps>;

let hubOfferModule: Promise<HubOfferComponent> | null = null;
let ResolvedHubOffer: HubOfferComponent | null = null;

/**
 * Loads the offer chunk once; a failure clears the cache so the next submit
 * retries. The island awaits this before it shows an answer.
 */
function loadHubOffer(): Promise<HubOfferComponent> {
  hubOfferModule ??= import("./NakshatraHubFinderOffer").then(
    (module) => (ResolvedHubOffer = module.default),
    (error: unknown) => {
      hubOfferModule = null;
      throw error;
    }
  );
  return hubOfferModule;
}

/** Warms the offer chunk on the submit; a failure leaves the island's wait to retry. */
function preloadHubOffer() {
  void loadHubOffer().catch(() => undefined);
}

/** Renders the loaded offer synchronously; nothing if its chunk failed to load. */
function HubOffer(props: AfterVerdictProps) {
  return ResolvedHubOffer ? <ResolvedHubOffer {...props} /> : null;
}

/** analytics placement of the hub finder (the slug pages report nakshatra_page_finder). */
const HUB_PLACEMENT = "nakshatra_hub_finder";

/** BirthToolFormShell input names -> the real form's input ids. */
const FORM_FIELD_IDS: Readonly<Record<string, string>> = {
  dob: "birthDate-default",
  tob: "birthTime-default",
  place: "birthPlace-default",
};

function formFieldId(target: EventTarget | null): string {
  const name = target instanceof Element ? target.getAttribute("name") : null;
  return (name && FORM_FIELD_IDS[name]) || FORM_FIELD_IDS.dob;
}

export default function NakshatraHubFinderLazy() {
  const { ready, activate } = useDeferredToolActivation({ scope: "element" });
  const [focusFieldId, setFocusFieldId] = useState<string | null>(null);

  // Focus follows the visitor into the real form after a keyboard focus, a
  // mouse press or a submit on the shell; never after a touch, which may be
  // the start of a scroll.
  const shellIntent = ready
    ? { onSubmitCapture: preloadHubOffer }
    : {
        onPointerDownCapture: (event: PointerEvent<HTMLDivElement>) =>
          setFocusFieldId(event.pointerType === "mouse" ? formFieldId(event.target) : null),
        onFocusCapture: (event: FocusEvent<HTMLDivElement>) =>
          setFocusFieldId(formFieldId(event.target)),
        onSubmitCapture: () => setFocusFieldId(FORM_FIELD_IDS.dob),
      };

  return (
    <div translate="no" data-nakshatra-hub-finder-slot="" className={SLOT_FLOORS} {...shellIntent}>
      {ready ? (
        <NakshatraFinder
          pageNakshatraId={null}
          placement={HUB_PLACEMENT}
          focusFieldId={focusFieldId}
          AfterVerdict={HubOffer}
          afterVerdictReady={loadHubOffer}
          frameClassName={SLOT_FLOORS}
          submitReserveClassName={SUBMIT_RESERVE}
        />
      ) : (
        <BirthToolFormShell {...SHELL_PROPS} onActivate={activate} />
      )}
    </div>
  );
}
